// vim: ft=typescript
import React, { useState, useEffect } from 'react'
import {
  Row,
  Col,
  Button,
  Form,
  Alert,
  Spinner,
} from 'react-bootstrap'
import useAuth from '../hooks/useAuth'
import useResident from '../hooks/useResident'

const MyAccount = () => {
  const {
    authError,
    changePassword,
  } = useAuth()

  const {
    firstName: savedFirstName,
    lastName: savedLastName,
    phone: savedPhone,
    unit: savedUnit,

    hasActivePhoneVerification,
    hasExpiredPhoneVerification,
    isPhoneVerified,
    requestPhoneVerification,
    verifyPhone,
    updateResident,
    fetchResident,
  } = useResident()

  const [firstName, setFirstName] = useState(savedFirstName)
  const [lastName, setLastName] = useState(savedLastName)
  const [phone, _setPhone] = useState(savedPhone)
  const [unit, setUnitNumber] = useState(savedUnit)
  const [status, setStatus] = useState('')
  const [statusType, setStatusType] = useState('info')

  const [oldPassword, setOldPassword] = useState('')

  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')

  const [saving, setSaving] = useState(false)

  const [showPasswords, setShowPasswords] = useState(false)

  const [verifyingPhone, setVerifyingPhone] = useState(hasActivePhoneVerification && !hasExpiredPhoneVerification)
  const [verificationCode, setVerificationCode] = useState('')
  const isValidVerificationCode = verificationCode.length === 6

  const setPhone = (newPhone: string): void => {
    if (newPhone) {
      _setPhone(newPhone.replace(/^1(.*)/, '$1'));
    } else {
      _setPhone(newPhone);
    }
  }

  useEffect(() => {
    setPhone(savedPhone)
  }, [savedPhone])

  const togglePasswordDisplay = () => {
    setShowPasswords(!showPasswords)
  }

  const canSave = (!!firstName && !!lastName && !!phone && (phone.length === 10) && !!unit && ((firstName !== savedFirstName) || (lastName !== savedLastName) || (phone !== savedPhone) || (unit !== savedUnit)))
  const canChangePassword = (!!oldPassword && !!password && !!passwordConfirm && (password === passwordConfirm) && password.length >= 8)
  const onChangePassword = async () => {
    setSaving(true)
    const result = await changePassword(oldPassword, password)

    if (result === 'SUCCESS') {
      setStatusType('success')
      setStatus('Password updated.')
      setOldPassword('')
      setPassword('')
      setPasswordConfirm('')
    }

    setSaving(false)
  }

  const onSave = async () => {
    setSaving(true)

    updateResident({ firstName, lastName, phone, unit })

    setSaving(false)
  }

  const isValidPhone = !!phone && phone.replace(/[^0-9]/g, '').length === 10
  const isValidUnit = /^[0-9]{1,2}$/.test(unit) && (unit.length === 1 || unit.length === 2)

  const onRequestPhoneVerification = async () => {
    setVerifyingPhone(true)
    await requestPhoneVerification()
    fetchResident()
  }

  const onVerifyPhone = async () => {
    verifyPhone(verificationCode)
  }

  if (saving) {
    return (
      <Spinner animation="border" role="status" variant="dark">
        <span className="visually-hidden">Signing Up...</span>
      </Spinner>
    )
  }

  let verificationAction

  if (hasExpiredPhoneVerification) {
    verificationAction = (
      <>
        <div className="mb-2">
          Your verification code has expired.
        </div>
        <Button variant="secondary" size="sm" disabled={verifyingPhone} onClick={onRequestPhoneVerification}>{verifyingPhone ? 'Resending...' : 'Resend Verification' }</Button>
      </>
    )
  } else if (verifyingPhone) {
    verificationAction = (
      <>
        <div className="mb-2 lead">
          A six digit code has been texted to {phone} from {process.env.REACT_APP_SOURCE_SMS}. It may take up to a minute to arrive.
        </div>

        <Form.Group controlId="code" className="mb-3">
          <Form.Label>Verification Code</Form.Label>
          <Form.Control
            type="number"
            value={verificationCode}
            className="w-25"
            isValid={!!verificationCode && isValidVerificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/, '').substring(0, 6))} />
        </Form.Group>

        <Button variant={isValidVerificationCode ? 'primary' : 'secondary'} size="sm" disabled={!isValidVerificationCode} onClick={onVerifyPhone}>Verify Code</Button>
      </>
    )
  } else {
    verificationAction = (
      <>
        <div className="mb-2">
          Your phone number must be verified to access certain features.
        </div>

        <Button variant="secondary" size="sm" onClick={onRequestPhoneVerification}>Request Verification</Button>
      </>
    )
  }

  return (
    <>
      <h1 className="mb-3">My Account</h1>

      <Form noValidate validated={canSave && isValidPhone && isValidUnit}>
        <h4>Update Information</h4>

        <Row>
          <Col sm="5">
            <Form.Group controlId="first-name" className="mb-3">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                type="text"
                value={firstName}
                isValid={!!firstName}
                onChange={(e) => setFirstName(e.target.value)} />
            </Form.Group>

          </Col>
          <Col sm="5">
            <Form.Group controlId="last-name" className="mb-3">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                type="text"
                value={lastName}
                isValid={!!lastName}
                onChange={(e) => setLastName(e.target.value)} />
            </Form.Group>
          </Col>
          <Col sm="2">
            <Form.Group controlId="unit" className="mb-3">
              <Form.Label>Unit Number</Form.Label>
              <Form.Control
                type="number"
                placeholder="e.g. 99"
                required
                value={unit}
                className="w-50"
                isValid={unit && isValidUnit}
                onChange={(e) => setUnitNumber(e.target.value.replace(/[^0-9]/g, ''))} />
              <Form.Control.Feedback type="invalid">Invalid Unit</Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col sm="5">
            <Form.Group controlId="phone" className="mb-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="phone"
                value={phone}
                isValid={phone && isValidPhone}
                onChange={(e) => setPhone(e.target.value)} />
              <Form.Text>Used for account recovery, communication about your bookings, and accessing the amenities building.</Form.Text>
            </Form.Group>
          </Col>

          {isPhoneVerified && (
            <Col sm="5">
              <Alert variant="success" className="mt-4">
                Your phone number has been verified and can be used to check into the lower level of the amenities building.
              </Alert>
            </Col>
          )}

          {!isPhoneVerified && (
            <Col sm="7">
              <Alert variant={verifyingPhone ? 'info' : 'warning'}>
                {verificationAction}
              </Alert>
            </Col>
          )}
        </Row>

        <Button variant="primary" size="lg" className="me-3" disabled={!canSave} onClick={onSave}>Save</Button>
      </Form>

      <hr />

      <Form noValidate validated={canChangePassword}>
        {authError && <Alert variant="danger">{authError}</Alert>}
        {status && <Alert variant={statusType}>{status}</Alert>}

        <h4>Change Password</h4>

        <p className="lead">If you want to change your password, enter your old password and then pick a new one.</p>

        <Row>
          <Col sm="4">
            <Form.Group className="mb-3" controlId="old-password">
              <Form.Label>Old Password</Form.Label>
              <Form.Control type={showPasswords ? 'text' : 'password'} value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} />
              <Form.Text><Button variant="link" onClick={togglePasswordDisplay}>{showPasswords ? 'Hide' : 'Show'} passwords</Button></Form.Text>
            </Form.Group>

          </Col>
          <Col sm="4">
            <Form.Group className="mb-3" controlId="password">
              <Form.Label>New Password</Form.Label>
              <Form.Control type={showPasswords ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} />
              <Form.Text>Must be at least 8 characters and contain upper and lower case letters as well as a number.</Form.Text>
            </Form.Group>

          </Col>
          <Col sm="4">
            <Form.Group className="mb-3" controlId="password-confirm">
              <Form.Label>Confirm New Password</Form.Label>
              <Form.Control type={showPasswords ? 'text' : 'password'} value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} />
            </Form.Group>
          </Col>
        </Row>

        <Button variant="primary" size="lg" className="me-3" disabled={!canChangePassword} onClick={onChangePassword}>Change Password</Button>
      </Form>

    </>
  )

}

export default MyAccount

